<template lang="pug">
.kr-brand-selector(:class="dynamicClasses")
  .kr-brand-selector-wrapper
    .kr-arrow
    ul.kr-brand-selector-list(
      ref="menu"
      role="menu"
      tabindex="-1"
      :id="menuUid"
      :aria-labelledby="buttonUid"
      :aria-activedescendant="activeItemUid"
      @keydown="onKeydown"
    )
      li(
        v-for="(config, idx) in configList"
        role="menuitem"
        tabindex="-1"
        :id="config.uid"
        :class="computeClasses(config)"
        :aria-label="config.brand"
        :data-forced-brand="config.brand.toLowerCase()"
        @click="onClick(config.brand)"
        @touchend="onClick(config.brand)"
      )
        SvgImage(
          v-if="config.icon"
          :aria-labelledby="config.uid"
          :title="config.brand"
          :decorative="false"
          :template="config.icon"
        )
</template>

<script>
import SvgImage from '@/common/components/SvgImage'

// Model
const event = 'update'

/**
 * Based on W3 example:
 * https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/examples/menu-button-actions-active-descendant/
 *
 * @since KJS-5014
 */
export default {
  name: 'CardBrandSelectorMenu',
  components: { SvgImage },
  model: {
    prop: 'selectedBrand',
    event
  },
  props: {
    // v-model
    selectedBrand: {
      default: undefined,
      required: false
    },
    selectorContext: {
      type: Object,
      required: true
    },
    buttonUid: {
      type: String,
      required: true
    },
    menuUid: {
      type: String,
      required: true
    },
    brandObjects: {
      type: Array,
      required: true
    },
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeItemIdx: 0
    }
  },
  computed: {
    dynamicClasses() {
      return {
        'kr-open': this.open
      }
    },
    configList() {
      return this.brandObjects.map(o => {
        return { ...o, uid: this.computeItemUid(o) }
      })
    },
    activeItemConfig() {
      return this.configList[this.activeItemIdx]
    },
    activeItemUid() {
      return this.activeItemConfig.uid
    },
    keyPressed: {
      get() {
        return this.selectorContext.keyPressed
      },
      set(value) {
        this.$emit('user-action', value)
      }
    }
  },
  watch: {
    open(val) {
      if (val === true) {
        this.onOpening()
      }
    }
  },
  methods: {
    computeClasses(brandObject) {
      return {
        'kr-selected-brand': brandObject.brand === this.selectedBrand,
        ['kr-selector-' + brandObject.brand.toLowerCase()]: true,
        'kr-active-option':
          brandObject.brand === this.activeItemConfig.brand && this.keyPressed
      }
    },
    computeItemUid(brandObject) {
      return `kr-cbs-item-${this._uid}-${brandObject.brand}`
    },
    onUpdate(value) {
      this.$emit(event, value)
    },
    onOpening() {
      this.activeItemIdx = 0
      this.$refs.menu.focus()
    },
    closeMenu() {
      this.$emit('close')
    },
    setCursor(idx) {
      this.activeItemIdx = idx
    },
    moveUp() {
      if (this.activeItemIdx <= 0) {
        this.activeItemIdx = this.configList.length - 1
      } else {
        this.activeItemIdx -= 1
      }
    },
    moveDown() {
      if (this.activeItemIdx >= this.configList.length - 1) {
        this.activeItemIdx = 0
      } else {
        this.activeItemIdx += 1
      }
    },
    onClick(brand) {
      this.keyPressed = false
      this.onUpdate(brand)
    },
    onKeydown(event) {
      this.keyPressed = true
      let flag = false

      if (event.ctrlKey || event.altKey || event.metaKey) {
        return
      }

      if (event.shiftKey) {
        if (event.key === 'Tab') {
          this.closeMenu()
          flag = true
        }
      } else {
        switch (event.key) {
          case ' ':
          case 'Enter':
            this.closeMenu()
            this.onUpdate(this.activeItemConfig.brand)
            flag = true
            break
          case 'Esc':
          case 'Escape':
            this.closeMenu()
            flag = true
            break

          case 'Up':
          case 'ArrowUp':
            this.moveUp()
            flag = true
            break
          case 'ArrowDown':
          case 'Down':
            this.moveDown()
            flag = true
            break
          case 'Tab':
            this.closeMenu()
            break
        }
      }

      if (flag) {
        event.stopPropagation()
        event.preventDefault()
      }
    }
  }
}
</script>
